import { AlertTriangle } from 'lucide-react';
import React, { useMemo } from 'react';
import { isRouteErrorResponse, useLocation, useNavigate, useRouteError } from 'react-router-dom';
import { useForgeTheme } from '../../forge/hooks/useForgeTheme';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { resolveDocumentTitle } from '../../navigation/documentTitle';
import { Button } from '../../ui/button';
import './route-error.css';
import { describeRouteError } from './routeErrorPresentation';

interface RouteErrorViewProps {
  error: unknown;
  status?: number;
  /** Where "Back to safety" goes when history has nothing useful. */
  homePath: string;
  homeLabel: string;
  variant: 'app' | 'workspace';
}

/**
 * The rendered body of both error elements.
 *
 * Recovery is deliberately plain: reload (the fix for a stale chunk), go back
 * (the fix for a bad deep link), and a link to a known-good page in the current
 * area. Nothing here re-renders the failed route in place: react-router has
 * already torn that subtree down, so "try again" without a navigation or reload
 * would be a lie.
 */
const RouteErrorView: React.FC<RouteErrorViewProps> = ({
  error,
  status,
  homePath,
  homeLabel,
  variant,
}) => {
  const navigate = useNavigate();
  const presentation = describeRouteError(error, status);

  return (
    <div
      className={`route-error route-error--${variant}`}
      role="alert"
      data-testid="route-error"
      data-error-kind={presentation.kind}
    >
      <AlertTriangle className="route-error__icon" aria-hidden="true" size={28} />
      <h1 className="route-error__title">{presentation.title}</h1>
      <p className="route-error__description">{presentation.description}</p>
      <div className="route-error__actions">
        <Button
          variant={presentation.reloadLikelyFixes ? 'default' : 'outline'}
          onClick={() => window.location.reload()}
        >
          Reload page
        </Button>
        <Button variant="outline" onClick={() => navigate(-1)}>
          Go back
        </Button>
        <Button
          variant={presentation.reloadLikelyFixes ? 'outline' : 'default'}
          onClick={() => navigate(homePath)}
        >
          {homeLabel}
        </Button>
      </div>
      <details className="route-error__detail">
        <summary>Technical details</summary>
        <pre>{presentation.detail}</pre>
      </details>
    </div>
  );
};

function useRouteErrorParts() {
  const error = useRouteError();
  return {
    error,
    status: isRouteErrorResponse(error) ? error.status : undefined,
  };
}

/**
 * Root error element. This is the last line of defence: it catches failures
 * from the layout elements themselves, so it cannot rely on a shell being
 * mounted. It therefore establishes its own `.forge-root` token scope, because
 * the theme variables only resolve inside one.
 */
export const AppErrorElement: React.FC = () => {
  const { error, status } = useRouteErrorParts();
  const { theme } = useForgeTheme();
  // This replaces the root shell, and the tab title writer with it, so the error
  // page has to name its own tab — an unmatched path resolves to its workspace.
  const { pathname } = useLocation();
  useDocumentTitle(useMemo(() => resolveDocumentTitle(pathname), [pathname]));

  return (
    <div className="forge-root" data-forge-theme={theme} data-testid="app-error-scope">
      <RouteErrorView
        error={error}
        status={status}
        homePath="/"
        homeLabel="Go to start"
        variant="app"
      />
    </div>
  );
};

/**
 * Error element for a workspace's pages. It is mounted on a pathless route
 * *inside* the workspace layout route, so a page crash replaces only the
 * content area: the sidebar, the app switcher and the theme toggle stay
 * mounted and usable.
 */
export const WorkspaceErrorElement: React.FC<{ homePath: string; homeLabel: string }> = ({
  homePath,
  homeLabel,
}) => {
  const { error, status } = useRouteErrorParts();

  return (
    <RouteErrorView
      error={error}
      status={status}
      homePath={homePath}
      homeLabel={homeLabel}
      variant="workspace"
    />
  );
};
